<template>
  <el-container id='wrapper'>
    <el-header>
      <PageTitle title="Summary"></PageTitle>
    </el-header>
    <el-main class="main">
      <div class="container">
        <UserSelector includeEveryone v-if="$store.getters.adminMode"></UserSelector>
        
        <el-select v-model="selectedSummaryType" value-key="attributes.name" placeholder="Summary Type" class="summary-select">
          <el-option
            v-for="type in summaryTypes"
            :key="type.index"
            :label="type.name"
            :value="type.value"
            :disabled="type.disabled"
            >
          </el-option>
        </el-select>
      
        <SummaryChartDoughnut v-loading="isLoading" :data="chartData" class="chart" :options="options"></SummaryChartDoughnut>
          
      </div>
    </el-main>
  </el-container>
</template>

<script>
import PageTitle from '../components/PageTitle'
import SummaryChart from '../components/SummaryChart.js'
import SummaryChartDoughnut from '../components/SummaryChartDoughnut.js'
import UserSelector from '../components/UserSelector'
import pattern from 'patternomaly'
import { arraysEqual, compareAttributes } from '@/utils/utils'
import { getDateRangeFromRangeIndex } from '@/utils/timeRange'

const options = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    position: 'bottom'
  },
  animation: {
    animateRotate: false,
    onProgress: function (animation) {
      var chartContext = this.chart.canvas.getContext('2d', {alpha: false})
      chartContext.fillStyle = '#4a4a4a'
      chartContext.font = (animation.chart.chartArea.bottom / 9) + 'px Helvetica'
      chartContext.textBaseline = 'middle'
      chartContext.textAlign = 'center'
      let data = this.data.datasets[0].data
      let sum = data => data.reduce((a, b) => a + b, 0)
      chartContext.fillText(sum(data).toFixed(1), animation.chart.chartArea.right / 2, animation.chart.chartArea.bottom / 2, animation.chart.chartArea.bottom / 4)
    },
    onComplete: function (animation) {
      var chartContext = this.chart.canvas.getContext('2d', {alpha: false})
      chartContext.fillStyle = '#4a4a4a'
      chartContext.font = (animation.chart.chartArea.bottom / 9) + 'px Helvetica'
      chartContext.textBaseline = 'middle'
      chartContext.textAlign = 'center'
      let data = this.data.datasets[0].data
      let sum = data => data.reduce((a, b) => a + b, 0)
      chartContext.fillText(sum(data).toFixed(1), animation.chart.chartArea.right / 2, animation.chart.chartArea.bottom / 2, animation.chart.chartArea.bottom / 4)
    }
  }
}

export default {
  components: {
    PageTitle,
    SummaryChart,
    UserSelector,
    SummaryChartDoughnut
  },
  data () {
    return {
      isLoading: false,
      options,
      chartProp: 'currentActivityName',
      chartDataDoughnut: {
        labels: ['skill1', 'skill2'],
        datasets: [
          {
            backgroundColor: ['blue', 'green'],
            data: [2, 4]
          }
        ]
      }
    }
  },
  computed: {
    selectedSummaryType: {
      get () {
        return this.$store.getters.summaryProp
      },
      set (prop) {
        this.$store.commit('setSummaryProp', prop)
      }
    },
    summaryTypes () {
      let ret = [
        {name: 'Client', index: 0, value: 'client'},
        {name: 'Project', index: 1, value: 'projectPath'},
        {name: 'Task', index: 2, value: 'taskName'},
        {name: 'Activity', index: 3, value: 'currentActivityName'},
        {name: 'Tag', index: 4, value: 'tags'}
      ]
      if (this.$store.getters.adminMode) {
        ret.push({name: 'User', index: 5, value: 'userName'})
      }
      return ret
    },
    timeEntries () {
      this.$store.commit('updateFilteredEntries', this.$store.getters.timeEntries)
      return this.$store.getters.timeEntries
    },
    isCurrentWeek: function () {
      return arraysEqual(this.$store.getters.currentDateRange, getDateRangeFromRangeIndex('week', 0))
    },
    chartData: function () {
      let attribute = this.$store.getters.summaryProp
      let results = []
      let entries = this.timeEntries || []
      entries.filter(entry => { return !entry.isPersonal }).forEach(obj => {
        let attr = obj[attribute]
        let duration = Number(obj.duration) || 0
        let found = results.findIndex(el => {
          let ret = el.attr === attr
          if (Array.isArray(el.attr) && Array.isArray(attr)) {
            ret = compareAttributes(el.attr, attr)
          }
          return ret
        })
        if (found !== -1) {
          results[found].duration = Number(Number(Number(results[found].duration) + duration).toFixed(1))
        } else {
          results.push({attr: attr, duration: Number(duration.toFixed(1)), entry: obj})
        }
      })

      results = results.sort((a, b) => (a.duration < b.duration) ? 1 : -1)
      const colors = ['#a9c397', '#a5bdda', '#dcadaf', '#d19ae1', '#dcca89', '#80ced9',
        '#efc09d', '#bebebe', '#c8b7a9', '#e7a7bd', '#63b598', '#ce7d78', '#ea9e70', '#a48a9e', '#c6e1e8', '#648177', '#0d5ac1',
        '#f205e6', '#1c0365', '#14a9ad', '#4ca2f9', '#a4e43f', '#d298e2', '#6119d0',
        '#d2737d', '#c0a43c', '#f2510e', '#651be6', '#79806e', '#61da5e', '#cd2f00',
        '#9348af', '#01ac53', '#c5a4fb', '#996635', '#b11573', '#4bb473', '#75d89e',
        '#2f3f94', '#2f7b99', '#da967d', '#34891f', '#b0d87b', '#ca4751', '#7e50a8',
        '#c4d647', '#e0eeb8', '#11dec1', '#289812', '#566ca0', '#ffdbe1', '#2f1179',
        '#935b6d', '#916988', '#513d98', '#aead3a', '#9e6d71', '#4b5bdc', '#0cd36d',
        '#250662', '#cb5bea', '#228916', '#ac3e1b', '#df514a', '#539397', '#880977',
        '#f697c1', '#ba96ce', '#679c9d', '#c6c42c', '#5d2c52', '#48b41b', '#e1cf3b',
        '#5be4f0', '#57c4d8', '#a4d17a', '#225b8', '#be608b', '#96b00c', '#088baf',
        '#f158bf', '#e145ba', '#ee91e3', '#05d371', '#5426e0', '#4834d0', '#802234',
        '#6749e8', '#0971f0', '#8fb413', '#b2b4f0', '#c3c89d', '#c9a941', '#41d158',
        '#fb21a3', '#51aed9', '#5bb32d', '#807fb', '#21538e', '#89d534', '#d36647',
        '#7fb411', '#0023b8', '#3b8c2a', '#986b53', '#f50422', '#983f7a', '#ea24a3',
        '#79352c', '#521250', '#c79ed2', '#d6dd92', '#e33e52', '#b2be57', '#fa06ec',
        '#1bb699', '#6b2e5f', '#64820f', '#1c271', '#21538e', '#89d534', '#d36647',
        '#7fb411', '#0023b8', '#3b8c2a', '#986b53', '#f50422', '#983f7a', '#ea24a3',
        '#79352c', '#521250', '#c79ed2', '#d6dd92', '#e33e52', '#b2be57', '#fa06ec',
        '#1bb699', '#6b2e5f', '#64820f', '#1c271', '#9cb64a', '#996c48', '#9ab9b7',
        '#06e052', '#e3a481', '#0eb621', '#fc458e', '#b2db15', '#aa226d', '#792ed8',
        '#73872a', '#520d3a', '#cefcb8', '#a5b3d9', '#7d1d85', '#c4fd57', '#f1ae16',
        '#8fe22a', '#ef6e3c', '#243eeb', '#1dc18', '#dd93fd', '#3f8473', '#e7dbce',
        '#421f79', '#7a3d93', '#635f6d', '#93f2d7', '#9b5c2a', '#15b9ee', '#0f5997',
        '#409188', '#911e20', '#1350ce', '#10e5b1', '#fff4d7', '#cb2582', '#ce00be',
        '#32d5d6', '#17232', '#608572', '#c79bc2', '#00f87c', '#77772a', '#6995ba',
        '#fc6b57', '#f07815', '#8fd883', '#060e27', '#96e591', '#21d52e', '#d00043',
        '#b47162', '#1ec227', '#4f0f6f', '#1d1d58', '#947002', '#bde052', '#e08c56',
        '#28fcfd', '#bb09b', '#36486a', '#d02e29', '#1ae6db', '#3e464c', '#a84a8f',
        '#911e7e', '#3f16d9', '#0f525f', '#ac7c0a', '#b4c086', '#c9d730', '#30cc49',
        '#3d6751', '#fb4c03', '#640fc1', '#62c03e', '#d3493a', '#88aa0b', '#406df9',
        '#615af0', '#4be47', '#2a3434', '#4a543f', '#79bca0', '#a8b8d4', '#00efd4',
        '#7ad236', '#7260d8', '#1deaa7', '#06f43a', '#823c59', '#e3d94c', '#dc1c06',
        '#f53b2a', '#b46238', '#2dfff6', '#a82b89', '#1a8011', '#436a9f', '#1a806a',
        '#4cf09d', '#c188a2', '#67eb4b', '#b308d3', '#fc7e41', '#af3101', '#ff065',
        '#71b1f4', '#a2f8a5', '#e23dd0', '#d3486d', '#00f7f9', '#474893', '#3cec35',
        '#1c65cb', '#5d1d0c', '#2d7d2a', '#ff3420', '#5cdd87', '#a259a4', '#e4ac44',
        '#1bede6', '#8798a4', '#d7790f', '#b2c24f', '#de73c2', '#d70a9c', '#25b67',
        '#88e9b8', '#c2b0e2', '#86e98f', '#ae90e2', '#1a806b', '#436a9e', '#0ec0ff',
        '#f812b3', '#b17fc9', '#8d6c2f', '#d3277a', '#2ca1ae', '#9685eb', '#8a96c6',
        '#dba2e6', '#76fc1b', '#608fa4', '#20f6ba', '#07d7f6', '#dce77a', '#77ecca']
      let data = []
      let dataColors = []
      let labels = []
      results.forEach((result) => {
        data.push(Number(Number(result.duration).toFixed(1)))
        if (result.entry.task && result.entry.task.attributes.tags) {
          if (result.entry.task.attributes.tags.includes('FluxPTO') || result.entry.task.attributes.tags.includes('FluxHoliday')) {
            dataColors.push(pattern.draw('diagonal', colors[dataColors.length]))
          } else {
            dataColors.push(colors[dataColors.length])
          }
        } else {
          dataColors.push(colors[dataColors.length])
        }

        labels.push(result.attr + ' [' + Number(result.duration).toFixed(1) + ']')
      })
      let ret = {
        labels: labels,
        datasets: [
          {
            backgroundColor: dataColors,
            data: data
          }
        ]
      }
      return ret
    },
    taskTime: function () {
      let time = 0
      let entries = this.timeEntries || []
      entries.forEach((date) => {
        if (!date.isPersonal && !date.isPTO && !date.isHoliday) {
          time += Number(date.duration)
        }
      })
      return time.toFixed(1)
    },
    paidTimeOff: function () {
      let time = 0
      let entries = this.timeEntries || []
      entries.forEach((date) => {
        if (date.isPTO) {
          time += Number(date.duration)
        }
      })
      return time.toFixed(1)
    },
    holidayTime: function () {
      let time = 0
      let entries = this.timeEntries || []
      entries.forEach((date) => {
        if (date.isHoliday) {
          time += Number(date.duration)
        }
      })
      return time.toFixed(1)
    },
    totalTime: function () {
      return (Number(this.taskTime) + Number(this.paidTimeOff) + Number(this.holidayTime)).toFixed(1)
    },
    timeEntryFilters () {
      return this.$store.getters.timeEntryFilters
    }
  },
  mounted () {
    this.isLoading = true
    this.$store.dispatch('time-entries/loadAll', this.$store.getters.timeEntryFilters).then(() => { this.isLoading = false })
  },

  watch: {
    timeEntryFilters: function () {
      this.isLoading = true
      this.$store.dispatch('time-entries/loadAll', this.$store.getters.timeEntryFilters).then(() => { this.isLoading = false })
    }
  }
}
</script>

<style lang='scss' scoped>
@import "../styles/common.scss";
.chart {
  padding:40px;
  flex-grow: 1;
  width:100% !important;
}
.summary-select{
  position:absolute;
  padding:20px 40px;
  width:100px !important;
  right:0px;
}
.main {
  display: flex;
  align-items: stretch;
  padding-top: 0px;

  .container {
    flex-grow: 1;
    display: flex;
    align-items: stretch;
    background-color: white;
    border-radius: 10px;
  }
  .summary{
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items:stretch;
    
    > div {
      padding: 20px;
    }
    .totals {
      display: flex;
      border-bottom: 1px solid #ebeef5;

      > div {
        width: 25%;
        text-align: center;
        color: rgba(0,0,0,0.6);
        padding: 15px 0px;

        &:last-child {
          border-left: 1px solid rgba(0,0,0,0.3);

          div:last-child {
            color: $light-red
          }
        }
        div:last-child {
          padding-top: 10px;
          font-size: 50px;
          color: rgba(0,0,0,0.8);
        }
      }
    }
    
    
    
    .el-card {    
      border-radius: 10px; 
      height:100%;
      margin:auto;
    }

    .el-col, .el-card__body {
      vertical-align: middle !important;
    }
  }
}
</style>
<style lang="scss">
#horizontalbar-chart{
  height:100% !important;
}
.chartjs-render-monitor{
  height:100% !important;
}
.summary-chart-card{
  .el-card__body{
    height: 100% !important;
  }
}
</style>
